import {createRef, RefObject, useCallback, useEffect, useMemo, useState} from 'react';
import {AnswerValue, ContactField, LeadFormPage, LeadFormState} from '@target-web/leadads-form-view';
import {LeadFormDataType} from 'api/types/LeadForm';
import {useUserInfo} from 'utils/userInfoContext';
import useAnalytics from './useAnalytics';
import {getContactInfoValue} from 'utils/utils';
import {FormFieldInner, LeadFormStateInner} from 'core/types';
import {FormFieldController} from 'utils/FormFieldController';

export interface LeadFormControllerProps {
    leadForm: LeadFormDataType;
}

const createFormState = (contactFields: ContactField[], pages: LeadFormPage[], onRefresh: () => void) => {
    return {
        answers: {},
        contactInfo: contactFields.reduce<Partial<Record<ContactField, FormFieldInner>>>((acc, item) => {
            acc[item] = new FormFieldController({
                fieldName: item,
                refreshF: onRefresh,
            });
            return acc;
        }, {}),
        emptyAnswersIds: [],
        answersRefs:
            pages[0]?.blocks?.reduce<Record<string, RefObject<HTMLDivElement>>>((res, cur) => {
                res[cur.id] = createRef<HTMLDivElement>();
                return res;
            }, {}) || {},
    };
};

function useLeadFormController({leadForm}: LeadFormControllerProps) {
    const [consentStatus, setConsentStatus] = useState(true);
    const contactFields = useMemo(() => leadForm.contact_fields, [leadForm.contact_fields]);
    const userInfo = useUserInfo();
    const {leadAnswerAnalyticEvent} = useAnalytics();

    const onRefresh = useCallback(() => {
        setFormState((s) => ({...s}));
    }, []);

    const [formState, setFormState] = useState<LeadFormStateInner>(
        createFormState(contactFields, leadForm.pages, onRefresh),
    );

    useEffect(() => {
        if (userInfo) {
            (Object.keys(formState.contactInfo) as ContactField[]).forEach((field) => {
                const filledValue = getContactInfoValue(userInfo, field);
                if (filledValue) {
                    formState.contactInfo[field]?.fill({
                        value: filledValue,
                        // добавляем hash для последующей расшифровки на беке
                        ...(field === 'phone' || field === 'email' ? {hash: userInfo[`${field}_hash`]} : {}),
                    });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const validate = () => {
        // валидируем все контактные поля
        (Object.keys(formState.contactInfo) as ContactField[]).forEach((field) => {
            formState.contactInfo[field]?.validate?.();
        });

        // собираем массив невалидных контактных полей
        const invalidContactsFields = (Object.keys(formState.contactInfo) as Array<ContactField>).reduce<
            Array<ContactField>
        >((res, key) => {
            const formField = formState.contactInfo[key];

            if (formField && !formField.isValid) {
                res.push(key);
            }

            return res;
        }, []);

        // собираем массив идентификаторов вопросов без ответов
        const emptyAnswersIds = Object.keys(formState.answers)
            .map((id) => {
                const value = formState.answers[id].value;
                const isFilled = value?.answer_ids?.length ? value.answer_ids.length > 0 : !!value?.answer_text;

                return isFilled ? undefined : id;
            })
            .filter(Boolean) as string[];

        setFormState((prevState) => ({
            ...prevState,
            emptyAnswersIds,
        }));

        return {
            isValid: !invalidContactsFields.length && !(leadForm.required_answers && emptyAnswersIds.length),
            invalidContactsFields,
            emptyAnswersIds,
        };
    };

    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            answers:
                leadForm.pages?.[0]?.blocks?.reduce((acc, item) => {
                    acc[item.id] = {
                        value: {},
                        onChange: (value: AnswerValue) => {
                            setFormState((prevState) => {
                                let emptyAnswersIds = prevState.emptyAnswersIds;
                                if (value.answer_ids?.length || !!value.answer_text) {
                                    emptyAnswersIds = emptyAnswersIds.filter((id) => id !== item.id);
                                }

                                const answ = prevState.answers[item.id];
                                if (answ) {
                                    return {
                                        ...prevState,
                                        emptyAnswersIds,
                                        answers: {
                                            ...prevState.answers,
                                            [item.id]: {
                                                ...answ,
                                                value,
                                            },
                                        },
                                    };
                                }
                                return prevState;
                            });
                        },
                        onAnalyticEvent: () => leadAnswerAnalyticEvent({block_id: item.id}),
                    };

                    return acc;
                }, {} as LeadFormState['answers']) ?? {},
            emptyAnswersIds: [],
            answersRefs: formState.answersRefs,
        }));
    }, [formState.answersRefs, leadForm, leadAnswerAnalyticEvent]);

    return {formState, userInfo, consentStatus, validate, setConsentStatus};
}

export default useLeadFormController;
